import { AlertCircle, Calendar, ClipboardList, ExternalLink } from "lucide-react";
import { type ReactNode, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Card, PageHeader } from "../../components/ui/patrones";
import { Aviso, Boton, Etiqueta, Tabla } from "../../components/ui/primitivos";
import {
  cambiarEstadoPresupuesto,
  convertirPresupuesto,
  fetchPresupuesto,
} from "../../lib/consola-api";
import { useSession } from "../../lib/SessionContext";
import { useDatos } from "../../lib/useDatos";
import { SeccionArtes } from "../produccion/adjuntos";
import { etiquetaDeEstado } from "../produccion/estados";
import { fecha, fechaHora } from "../produccion/orden";
import { otsVivasBajoCancelado, pesos, subtotalDeItemGuardado } from "./consulta";
import { etiquetaDeEstadoConsulta, TONO } from "./vistas";

/**
 * El detalle de una consulta, porteado de `BudgetDetail.tsx` (ADR 0064).
 *
 * **Es una pantalla y no un modal.** El modal que tenía mostraba cuatro
 * columnas de renglones y el arte, y nada más: faltaban los contactos del
 * cliente, la forma de pago, el «válido hasta», el tamaño y la base de cada
 * renglón, el subtotal por renglón, el Total General, el motivo de cancelación
 * y las OTs que salieron de la consulta. Todo eso está en `imprenta` y no
 * entraba en 512 px, así que el port lo había dejado afuera.
 */
export function ConsultaPagina() {
  const { id = "" } = useParams<{ id: string }>();
  const navegar = useNavigate();
  const { can, me } = useSession();

  const detalle = useDatos(() => fetchPresupuesto(id), id);
  const [error, setError] = useState<string | null>(null);
  const [trabajando, setTrabajando] = useState(false);

  async function accion(fn: () => Promise<unknown>) {
    setTrabajando(true);
    setError(null);
    try {
      await fn();
      await detalle.recargar();
    } catch (err) {
      setError(err instanceof Error ? err.message : "La acción falló");
    } finally {
      setTrabajando(false);
    }
  }

  if (detalle.error) {
    return (
      <div className="space-y-6">
        <PageHeader titulo="Consulta" alVolver={() => navegar("/presupuestos")} />
        <Aviso>{detalle.error}</Aviso>
      </div>
    );
  }

  const datos = detalle.datos;
  if (!datos) {
    return <p className="p-8 text-center text-neutral-500">Cargando consulta…</p>;
  }

  const { presupuesto, items, relacionados: rel, producciones } = datos;
  const estado = presupuesto.estado;
  const otsVivas = otsVivasBajoCancelado(estado, producciones);

  // Editar es de quien la cargó, salvo `presupuestos.update_all` (ADR 0015), y
  // solo en BORRADOR (ADR 0028). El worker aplica la misma regla: esto solo
  // evita ofrecer un botón que va a dar 403.
  const puedeEditar =
    datos.editable &&
    can("presupuestos", "update") &&
    (can("presupuestos", "update_all") || presupuesto.creadoPorId === me?.id);

  return (
    <div className="space-y-6">
      <PageHeader
        titulo={presupuesto.numero ?? "Consulta"}
        subtitulo="Detalle de la consulta"
        alVolver={() => navegar("/presupuestos")}
        acciones={
          <Acciones
            estado={estado}
            puedeEditar={puedeEditar}
            trabajando={trabajando}
            itemsPendientes={datos.itemsPendientes}
            alEditar={() => navegar(`/presupuestos/${id}/editar`)}
            accion={accion}
            id={id}
          />
        }
      />

      {error ? <Aviso>{error}</Aviso> : null}

      <div className="grid gap-6 md:grid-cols-2">
        <Seccion titulo="Información del cliente">
          <div>
            <p className="text-neutral-500 text-sm">Cliente</p>
            <p className="flex items-center gap-2 font-medium text-lg">
              {rel.cliente}
              {rel.clienteEsVip ? <Etiqueta tono="ok">VIP</Etiqueta> : null}
            </p>
          </div>
          {rel.contactos.length > 0 ? (
            <div>
              <p className="mb-2 text-neutral-500 text-sm">Contactos</p>
              <div className="space-y-2">
                {rel.contactos.map((c) => (
                  <div key={c.id} className="border-neutral-200 border-l-2 pl-3 text-sm">
                    <p className="font-medium">{c.nombre ?? "Sin nombre"}</p>
                    {c.email ? <p className="text-neutral-500">{c.email}</p> : null}
                    {c.telefono ? <p className="text-neutral-500">{c.telefono}</p> : null}
                    {c.observacion ? (
                      <p className="text-neutral-500 text-xs italic">{c.observacion}</p>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </Seccion>

        <Seccion titulo="Estado y fechas">
          <div>
            <p className="mb-1 text-neutral-500 text-sm">Estado</p>
            <Etiqueta tono={TONO[estado] ?? "neutral"}>{etiquetaDeEstadoConsulta(estado)}</Etiqueta>
          </div>
          <ConFecha etiqueta="Fecha de creación" valor={fecha(presupuesto.creadoEn)} />
          {presupuesto.validoHasta ? (
            <ConFecha etiqueta="Válido hasta" valor={fecha(presupuesto.validoHasta)} />
          ) : null}
          <div>
            <p className="text-neutral-500 text-sm">Forma de pago</p>
            <p className="font-medium text-sm">{rel.formaPago ?? "Sin definir"}</p>
          </div>
          <div>
            <p className="text-neutral-500 text-sm">Cargada por</p>
            <p className="font-medium text-sm">{rel.creadoPor}</p>
          </div>
        </Seccion>
      </div>

      {estado === "CANCELADO" && presupuesto.observacionCancelacion ? (
        <Card className="space-y-2 border-red-200 bg-red-50 p-4">
          <h2 className="flex items-center gap-2 font-semibold text-red-800 text-sm">
            <AlertCircle className="h-5 w-5" />
            Consulta cancelada
          </h2>
          <div>
            <p className="font-medium text-red-700 text-sm">Motivo:</p>
            <p className="text-red-900">{presupuesto.observacionCancelacion}</p>
          </div>
          {presupuesto.fechaCancelacion ? (
            <div>
              <p className="font-medium text-red-700 text-sm">Fecha:</p>
              <p className="text-red-900">{fechaHora(presupuesto.fechaCancelacion)}</p>
            </div>
          ) : null}
          {rel.canceladoPor ? (
            <div>
              <p className="font-medium text-red-700 text-sm">Cancelada por:</p>
              <p className="text-red-900">{rel.canceladoPor}</p>
            </div>
          ) : null}
        </Card>
      ) : null}

      <Seccion titulo="Renglones">
        {/* `Tabla` ya trae su propio `overflow-x-auto`: son siete columnas y en
            una pantalla angosta se scrollean solas. */}
        <Tabla
          cabeceras={[
            "Producto",
            "Tamaño",
            "Base",
            { texto: "Cantidad", derecha: true },
            { texto: "Unitario", derecha: true },
            { texto: "Total", derecha: true },
            "Origen",
          ]}
        >
          {items.map((i) => {
            const subtotal = subtotalDeItemGuardado(i);
            return (
              <tr key={i.id}>
                <td className="px-4 py-2 font-medium">{i.producto ?? i.productoNombre ?? "—"}</td>
                <td className="px-4 py-2 text-neutral-500">{i.tamano ?? "—"}</td>
                <td className="px-4 py-2 text-neutral-500">{i.base ?? "—"}</td>
                <td className="px-4 py-2 text-right tabular-nums">{i.cantidad}</td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {i.precioUnitario === null ? <Pendiente /> : pesos(Number(i.precioUnitario))}
                </td>
                <td className="px-4 py-2 text-right tabular-nums">
                  {subtotal === null ? <Pendiente /> : pesos(subtotal)}
                </td>
                <td className="px-4 py-2">
                  {/* El snapshot es lo que permite explicar el importe dentro
                        de un año; sin él, el precio se escribió a mano. */}
                  <Etiqueta tono={i.precioSnapshot ? "ok" : "aviso"}>
                    {i.precioSnapshot ? "tarifa congelada" : "precio manual"}
                  </Etiqueta>
                </td>
              </tr>
            );
          })}
        </Tabla>

        {/* El Total General sale del `montoTotal` que calculó el worker, no de
            sumar los renglones acá: la regla de qué pasa cuando falta un precio
            —el total es `null`, no la suma parcial— vive en un solo lado. */}
        <div className="flex items-center justify-between border-neutral-200 border-t pt-3">
          <span className="font-semibold text-base">Total General</span>
          {presupuesto.montoTotal === null ? (
            <span className="text-amber-600">Pendiente de cotización</span>
          ) : (
            <span className="font-semibold text-brand-600 text-xl tabular-nums">
              {pesos(Number(presupuesto.montoTotal))}
            </span>
          )}
        </div>
      </Seccion>

      {/* El arte cuelga del presupuesto y SIGUE a la OT al convertirlo: es la
          razón por la que la relación es polimórfica y no una FK (ADR 0011). */}
      <Seccion titulo="Arte adjunto">
        <SeccionArtes entidadTipo="PRESUPUESTO" entidadId={id} />
      </Seccion>

      {otsVivas.length > 0 ? (
        <Aviso>
          Esta consulta está cancelada pero tiene{" "}
          {otsVivas.length === 1 ? "una OT viva" : `${otsVivas.length} OTs vivas`} (revividas por
          salto forzado): {otsVivas.map((o) => o.numero).join(", ")}.
        </Aviso>
      ) : null}

      {producciones.length > 0 && can("produccion", "read") ? (
        <Seccion titulo="Órdenes de trabajo" icono={<ClipboardList className="h-5 w-5" />}>
          <Tabla cabeceras={["Número", "Producto", "Estado", ""]}>
            {producciones.map((o) => (
              <tr
                key={o.id}
                className="cursor-pointer hover:bg-neutral-50"
                onClick={() => navegar(`/produccion/${o.id}`)}
              >
                <td className="px-4 py-2 font-mono text-sm">{o.numero}</td>
                <td className="px-4 py-2">{o.producto}</td>
                <td className="px-4 py-2">
                  <Etiqueta tono={otsVivas.includes(o) ? "aviso" : "neutral"}>
                    {etiquetaDeEstado(o.estado)}
                  </Etiqueta>
                </td>
                <td className="px-4 py-2 text-right">
                  <ExternalLink className="ml-auto h-4 w-4 text-neutral-400" />
                </td>
              </tr>
            ))}
          </Tabla>
        </Seccion>
      ) : null}
    </div>
  );
}

/**
 * Los botones del encabezado, que dependen del estado.
 *
 * Salen de la máquina de estados que aplica el worker, no de una copia: acá
 * solo se decide cuál OFRECER, y el endpoint vuelve a validarlo.
 */
function Acciones({
  estado,
  puedeEditar,
  trabajando,
  itemsPendientes,
  alEditar,
  accion,
  id,
}: {
  estado: string;
  puedeEditar: boolean;
  trabajando: boolean;
  itemsPendientes: number;
  alEditar: () => void;
  accion: (fn: () => Promise<unknown>) => Promise<void>;
  id: string;
}) {
  const { can } = useSession();

  return (
    <div className="flex flex-wrap gap-2">
      {estado === "BORRADOR" && puedeEditar ? (
        <Boton variante="secundario" onClick={alEditar}>
          Editar
        </Boton>
      ) : null}
      {estado === "BORRADOR" ? (
        <Boton
          disabled={trabajando}
          onClick={() => accion(() => cambiarEstadoPresupuesto(id, "PENDIENTE"))}
        >
          Enviar a aprobación
        </Boton>
      ) : null}
      {/* Aprobar y rechazar exigen `presupuestos.aprobar`, que Ventas no tiene. */}
      {estado === "PENDIENTE" && can("presupuestos", "aprobar") ? (
        <>
          <Boton
            variante="peligro"
            disabled={trabajando}
            onClick={() => accion(() => cambiarEstadoPresupuesto(id, "RECHAZADO"))}
          >
            Rechazar
          </Boton>
          <Boton
            disabled={trabajando}
            onClick={() => accion(() => cambiarEstadoPresupuesto(id, "APROBADO"))}
          >
            Aprobar
          </Boton>
        </>
      ) : null}
      {/* Sin renglones pendientes no hay nada que convertir: convertir es
          idempotente por renglón, y `imprenta` esconde el botón en ese caso en
          vez de ofrecer una acción que no haría nada. */}
      {estado === "APROBADO" && itemsPendientes > 0 && can("presupuestos", "convertir") ? (
        <Boton disabled={trabajando} onClick={() => accion(() => convertirPresupuesto(id))}>
          Convertir a {itemsPendientes} {itemsPendientes === 1 ? "OT" : "OTs"}
        </Boton>
      ) : null}
      {["BORRADOR", "PENDIENTE", "APROBADO", "EN_PRODUCCION"].includes(estado) ? (
        <Boton
          variante="peligro"
          disabled={trabajando}
          onClick={() => {
            // Cancelar exige explicación: sin ella el historial dice que se
            // canceló pero no por qué.
            const observacion = window.prompt("¿Por qué se cancela?");
            if (observacion)
              void accion(() => cambiarEstadoPresupuesto(id, "CANCELADO", observacion));
          }}
        >
          Cancelar
        </Boton>
      ) : null}
    </div>
  );
}

/** Una tarjeta con título, como las `Card` de `BudgetDetail`. */
function Seccion({
  titulo,
  icono,
  children,
}: {
  titulo: string;
  icono?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card className="space-y-3 p-4">
      <h2 className="flex items-center gap-2 font-semibold text-neutral-900 text-sm">
        {icono}
        {titulo}
      </h2>
      {children}
    </Card>
  );
}

/** Etiqueta arriba, fecha con su ícono abajo — el par de «Estado y fechas». */
function ConFecha({ etiqueta, valor }: { etiqueta: string; valor: string }) {
  return (
    <div>
      <p className="text-neutral-500 text-sm">{etiqueta}</p>
      <p className="mt-1 flex items-center gap-2 font-medium text-sm">
        <Calendar className="h-4 w-4 text-neutral-400" />
        {valor}
      </p>
    </div>
  );
}

/** Lo que va donde falta el precio. En ámbar, como `imprenta`: es una tarea. */
function Pendiente() {
  return <span className="text-amber-600 text-sm">Pendiente</span>;
}
